/* ─── Recursive component renderer ─── */

import { Component, memo, useMemo, type CSSProperties, type ErrorInfo, type ReactNode } from 'react';
import { useResolvedProps } from './store';
import { COMPONENT_REGISTRY } from './registry';

// ─── Error boundary for individual artifact components ───────────────

interface NodeErrorBoundaryProps {
  cuid: number;
  componentName: string;
  children: ReactNode;
}

interface NodeErrorBoundaryState {
  error: Error | null;
}

class NodeErrorBoundary extends Component<NodeErrorBoundaryProps, NodeErrorBoundaryState> {
  state: NodeErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): NodeErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[Artifact] ${this.props.componentName}#${this.props.cuid} crashed:`,
      error,
      info.componentStack,
    );
  }

  componentDidUpdate(prevProps: NodeErrorBoundaryProps) {
    if (this.state.error && prevProps.cuid !== this.props.cuid) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: '10px 14px',
            background: '#1c1017',
            border: '1px solid #5c2d2d',
            borderRadius: 8,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 11,
            lineHeight: 1.6,
          }}
        >
          <div style={{ fontWeight: 600, color: '#fca5a5', marginBottom: 4, fontSize: 12 }}>
            Render Error — {this.props.componentName}
          </div>
          <div style={{ color: '#f87171', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {this.state.error.message}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── RenderNode ──────────────────────────────────────────────────────

/**
 * Render a single node from the artifact component tree.
 *
 * Uses `useResolvedProps` which subscribes to the store and only
 * re-renders when the node's own resolved property values change.
 * Bound properties (Refs to other components or data model keys)
 * are resolved inside the snapshot, so changes to unrelated
 * components or keys are invisible to this node.
 *
 * Child components referenced in `subs` are rendered as nested
 * `<RenderNode>` elements, giving each child its own subscription.
 *
 * Wrapped in a per-node error boundary so a crash in one component
 * surfaces inline instead of tearing down the whole artifact panel.
 */
export const RenderNode = memo(function RenderNode({ cuid }: { cuid: number }) {
  const { node, resolved } = useResolvedProps(cuid);

  const resolvedProps = useMemo(() => {
    if (!node || !resolved) return null;
    if (Object.keys(node.subs).length === 0) return resolved;
    // Inject rendered children from subs
    const withSubs = { ...resolved };
    for (const [name, ref] of Object.entries(node.subs)) {
      if (Array.isArray(ref)) {
        withSubs[name] = ref.map((childCuid) => (
          <RenderNode key={childCuid} cuid={childCuid} />
        ));
      } else if (typeof ref === 'object' && ref !== null) {
        // dict subs: {key: cuid} → {key: <RenderNode>}
        const dict: Record<string, ReactNode> = {};
        for (const [k, childCuid] of Object.entries(ref as Record<string, number>)) {
          dict[k] = <RenderNode key={childCuid} cuid={childCuid} />;
        }
        withSubs[name] = dict;
      } else {
        withSubs[name] = <RenderNode key={ref} cuid={ref} />;
      }
    }
    return withSubs;
  }, [node, resolved]);

  if (!node || !resolvedProps) {
    return null;
  }

  const Comp = COMPONENT_REGISTRY[node.component];
  if (!Comp) {
    return (
      <div
        style={{
          padding: '8px 12px',
          background: '#fff3cd',
          color: '#856404',
          borderRadius: 6,
          fontSize: 12,
          fontFamily: 'var(--artifact-font-mono)',
        }}
      >
        Unknown component: <strong>{node.component}</strong>
      </div>
    );
  }

  // Components that participate in the parent Row/Column's flex layout
  // and may declare a `'full'` axis to mean "grow to fill". The flex
  // attribute has to live on *this* wrapper (the direct flex child seen
  // by the parent), not on the component's own root, which is one level
  // too deep to be reached by the flex algorithm.
  const FLEX_GROW_COMPONENTS = new Set(['Spacer', 'ColorBlock', 'Image']);
  const grows =
    FLEX_GROW_COMPONENTS.has(node.component) &&
    (resolvedProps.width === 'full' ||
      resolvedProps.height === 'full' ||
      resolvedProps.size === 'full');
  const wrapperStyle: CSSProperties = grows
    ? { display: 'flex', flex: 1, alignSelf: 'stretch', minWidth: 0 }
    : { minWidth: 0 };

  return (
    <NodeErrorBoundary cuid={cuid} componentName={node.component}>
      <div
        className="a-node"
        data-component={node.component}
        data-id={String(cuid)}
        style={wrapperStyle}
      >
        <Comp {...resolvedProps} __node_id={String(cuid)} __clock={node.clock} />
      </div>
    </NodeErrorBoundary>
  );
});
